import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  GroupedVirtuoso,
  GroupedVirtuosoHandle,
  Virtuoso,
} from 'react-virtuoso';

import {
  List,
  ListSubheader,
  VirtuosoScroller,
  Box,
} from '@cast/design-system';
import { Organization, OrganizationType } from '@cast/types';

import { buildOrganizationGroups } from '@oneclick/features/organization/management/utils/org-grouping';

import { NoOrganizationsFound } from './NoOrganizationsFound';
import {
  EnterpriseListItem,
  DefaultOrganizationListItem,
  ChildOrganizationListItem,
} from './OrganizationListItems';

const LIST_HEIGHT = 480;

type OrganizationListProps = {
  organizations?: Organization[];
  organizationFilter?: string;
  selectedEnterprise?: Organization;
  activeOrganizationId?: string;
  isMenuOpen?: boolean;
  onSelectEnterprise: (enterprise: Organization) => void;
  onSelectOrganization: (organization: Organization) => void;
};

export const OrganizationList = ({
  organizations,
  organizationFilter,
  selectedEnterprise,
  activeOrganizationId,
  isMenuOpen,
  onSelectEnterprise,
  onSelectOrganization,
}: OrganizationListProps) => {
  const [height, setHeight] = useState<number>(LIST_HEIGHT);
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const visibleRangeRef = useRef<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: 0,
  });

  // Optimized filtering and categorization in a single pass
  const {
    groups,
    filteredChildren,
    enterpriseChildCounts,
    shouldShowNoOrganizations,
    selectedEnterpriseChildrenCount,
  } = useMemo(() => {
    const baseOrganizations = organizations || [];

    // Single pass categorization and sorting
    const enterprises: Organization[] = [];
    const escalationEnterprises: Organization[] = [];
    const defaults: Organization[] = [];
    const escalationDefaults: Organization[] = [];
    const internalEnterprises: Organization[] = [];
    const internalDefaults: Organization[] = [];
    const children: Organization[] = [];
    const childCounts = new Map<string, number>();

    // First pass: categorize and count children for enterprises
    for (const org of baseOrganizations) {
      // Apply search filter if needed
      const isSatisfiedByFilter = organizationFilter
        ? org.name.toLowerCase().includes(organizationFilter.toLowerCase()) ||
          org.id === organizationFilter
        : true;

      switch (org.type) {
        case OrganizationType.ENTERPRISE:
          if (isSatisfiedByFilter) {
            if (org.organizationMember === false && org.internal) {
              internalEnterprises.push(org);
            } else if (org.organizationMember === false) {
              escalationEnterprises.push(org);
            } else {
              enterprises.push(org);
            }
          }
          break;
        case OrganizationType.DEFAULT:
          if (isSatisfiedByFilter) {
            if (org.organizationMember === false && org.internal) {
              internalDefaults.push(org);
            } else if (org.organizationMember === false) {
              escalationDefaults.push(org);
            } else {
              defaults.push(org);
            }
          }
          break;
        case OrganizationType.CHILD:
          if (org.parentId === selectedEnterprise?.id && isSatisfiedByFilter) {
            children.push(org);
          }

          // Count children for all enterprises (for display purposes)
          if (org.parentId) {
            childCounts.set(
              org.parentId,
              (childCounts.get(org.parentId) || 0) + 1
            );
          }
          break;
      }
    }

    // Sort defaults and children to prioritize active organization
    const sortByActive = (a: Organization, b: Organization) => {
      if (a.id === activeOrganizationId) return -1;
      if (b.id === activeOrganizationId) return 1;
      return 0;
    };

    defaults.sort(sortByActive);
    escalationDefaults.sort(sortByActive);
    internalDefaults.sort(sortByActive);
    children.sort(sortByActive);

    const builtGroups = buildOrganizationGroups({
      enterprises,
      escalationEnterprises,
      internalEnterprises,
      defaults,
      escalationDefaults,
      internalDefaults,
    });

    // Calculate derived values for parent component
    const totalItems = builtGroups.reduce((sum, g) => sum + g.items.length, 0);
    const shouldShowNoOrgs =
      !(totalItems || selectedEnterprise) ||
      (selectedEnterprise && !children?.length);

    const selectedEnterpriseChildrenCount = selectedEnterprise
      ? childCounts.get(selectedEnterprise.id) || 0
      : 0;

    return {
      groups: builtGroups,
      filteredChildren: children,
      enterpriseChildCounts: childCounts,
      shouldShowNoOrganizations: shouldShowNoOrgs,
      selectedEnterpriseChildrenCount: selectedEnterpriseChildrenCount,
    };
  }, [
    organizations,
    organizationFilter,
    selectedEnterprise,
    activeOrganizationId,
  ]);

  // Group counts for virtualized list
  const groupCounts = useMemo(
    () => groups.map((g) => g.items.length),
    [groups]
  );

  // Flat data for virtualized list
  const groupData = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const handleListHeight = useCallback((h: number) => {
    setHeight(h);
  }, []);

  // Auto-scroll to the active organization when menu opens
  useEffect(() => {
    if (!isMenuOpen || !activeOrganizationId || !virtuosoRef.current) {
      return;
    }

    // Don't scroll if user is filtering
    if (organizationFilter) {
      return;
    }

    // Don't scroll in drill-down view
    if (selectedEnterprise) {
      return;
    }

    // Check if active org is a default organization
    const activeOrg = organizations?.find(
      (org) => org.id === activeOrganizationId
    );
    if (activeOrg?.type !== OrganizationType.DEFAULT) {
      return; // Don't scroll for enterprises or child orgs
    }

    // Find which group contains the active org.
    // Group index is dynamic (not hardcoded) because groups are only
    // included when non-empty, so their indices shift.
    let activeGroupIndex = -1;
    let itemsBeforeActiveGroup = 0;
    let indexInGroup = -1;

    for (let i = 0; i < groups.length; i++) {
      const itemIndex = groups[i].items.findIndex(
        (org) => org.id === activeOrganizationId
      );
      if (itemIndex !== -1) {
        activeGroupIndex = i;
        indexInGroup = itemIndex;
        break;
      }
      itemsBeforeActiveGroup += groups[i].items.length;
    }

    // Skip if not found, or if it's in the first group (already visible at top)
    if (activeGroupIndex === -1 || activeGroupIndex === 0) {
      return;
    }

    // Calculate the global index of the active organization
    const activeOrgGlobalIndex = itemsBeforeActiveGroup + indexInGroup;

    // Check if the active organization is already visible
    // Read from ref.current to get the latest visible range
    const { startIndex, endIndex } = visibleRangeRef.current;

    if (
      activeOrgGlobalIndex >= startIndex &&
      activeOrgGlobalIndex <= endIndex
    ) {
      return; // Already visible, no need to scroll
    }

    // Scroll to the active org using its flat index across all groups
    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      virtuosoRef.current?.scrollToIndex({
        index: activeOrgGlobalIndex,
        behavior: 'auto', // Instant scroll
        align: 'center',
      });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [
    isMenuOpen,
    activeOrganizationId,
    groups,
    organizations,
    organizationFilter,
    selectedEnterprise,
  ]);

  const renderChildOrganization = useCallback(
    (index: number) => {
      const organization = filteredChildren[index];
      return (
        <ChildOrganizationListItem
          organization={organization}
          isActive={activeOrganizationId === organization.id}
          isBlocked={organization.blockEscalatedPrivilege}
          onSelect={onSelectOrganization}
        />
      );
    },
    [filteredChildren, activeOrganizationId, onSelectOrganization]
  );

  // Show NoOrganizationsFound if no organizations to display
  if (shouldShowNoOrganizations) {
    return (
      <NoOrganizationsFound
        selectedEnterprise={selectedEnterprise}
        selectedEnterpriseChildrenCount={selectedEnterpriseChildrenCount}
      />
    );
  }

  if (selectedEnterprise && filteredChildren.length > 0) {
    return (
      <>
        <ListSubheader>child organizations</ListSubheader>
        <Virtuoso
          totalListHeightChanged={handleListHeight}
          data={filteredChildren}
          style={{
            height,
            minHeight: 40,
            maxHeight: LIST_HEIGHT,
          }}
          overscan={LIST_HEIGHT}
          itemContent={renderChildOrganization}
          components={{
            Scroller: VirtuosoScroller,
          }}
          role="menu"
          aria-label="Child organizations list"
        />
      </>
    );
  }

  if (groups.length > 0 && !selectedEnterprise) {
    return (
      <List size="medium" disablePadding testId="existing-organizations">
        <GroupedVirtuoso
          key={organizationFilter ? 'filtered' : 'default'} // Prevent list to randomly scroll when searching
          ref={virtuosoRef}
          totalListHeightChanged={handleListHeight}
          style={{
            height,
            maxHeight: LIST_HEIGHT,
          }}
          overscan={LIST_HEIGHT}
          groupCounts={groupCounts}
          rangeChanged={(range) => {
            // Update ref synchronously without causing re-renders
            visibleRangeRef.current = {
              startIndex: range.startIndex,
              endIndex: range.endIndex,
            };
          }}
          groupContent={(index) => {
            const group = groups[index];
            return group.label ? (
              <ListSubheader>{group.label}</ListSubheader>
            ) : (
              // Needed as Virtuoso requires a non-zero height for the list
              <Box sx={{ minHeight: '1px' }} />
            );
          }}
          itemContent={(index) => {
            const item = groupData[index];

            // GroupedVirtuoso passes a flat index across all groups.
            // Walk through group sizes to find which group this index falls into
            // (e.g. if group 0 has 10 items and group 1 has 5, index 12 is in group 1)
            let groupIndex = 0;
            let itemsBefore = 0;
            for (let i = 0; i < groups.length; i++) {
              if (index < itemsBefore + groups[i].items.length) {
                groupIndex = i;
                break;
              }
              itemsBefore += groups[i].items.length;
            }
            const group = groups[groupIndex];

            if (item.type === OrganizationType.ENTERPRISE) {
              const organizationsCount =
                enterpriseChildCounts.get(item.id) || 0;

              return (
                <EnterpriseListItem
                  organization={item}
                  organizationsCount={organizationsCount}
                  isEscalation={group.isEscalation}
                  isBlocked={item.blockEscalatedPrivilege}
                  onSelect={onSelectEnterprise}
                />
              );
            } else {
              return (
                <DefaultOrganizationListItem
                  organization={item}
                  isActive={activeOrganizationId === item.id}
                  isEscalation={group.isEscalation}
                  isBlocked={item.blockEscalatedPrivilege}
                  onSelect={onSelectOrganization}
                />
              );
            }
          }}
          components={{
            Scroller: VirtuosoScroller,
          }}
        />
      </List>
    );
  }

  return null;
};
